'use client'

import { RequestValidationTriggers, TypeOfValidations } from '../../../../constants/validation-constants'
import {
    ContainersForValidatableInputs,
    getArrayOfValidatableInputsFromSource,
} from '../../../../helpers/getArrayOfValidatableInputsFromSource'
import { RefObject, useEffect } from 'react'
import { EventDispatchQueue } from '../../../UseEventDispatchQueue'
import { setupRequestPartialValidationEvent } from '../../../../helpers/setupCustomEvents'
import { dispatchRequestValidationEvent } from '../helpers/dispatchRequestValidationEvent'
import { assert } from '../../../../../Helpers/logging'
import { InputWithValidation, RequestValidationEventDetail } from '../../../../types/EventDetailTypes'

const DISPATCH_DELAY_INPUT_TRIGGER = 200
const DISPATCH_DELAY_CHANGE_TRIGGER = 10

/**
 * @memberof useValidationOnFields
 * @description
 * attaches listeners to inputs that dispatch CustomEvents<RequestValidationEventDetail>
 *
 * @listen 'change'
 * @listen 'input'
 * @fires RequestPartialValidation
 * @fires RequestPartialValidation
 *
 * @param inputs -- the list of inputs that will dispatch CustomEvents
 * @param on -- the trigger that will dispatch the CustomEvent
 * @param typeOfValidation -- instructs either silentvalidation or validation with errors
 * @param previousEvents -- reference to the register of active events. Will be mutated
 * */
export const useDispatchRequestValidationEvent = (
    source: NodeList | null | RefObject<ContainersForValidatableInputs | null>,
    on: RequestValidationTriggers,
    typeOfValidation: TypeOfValidations,
    queue: EventDispatchQueue<RequestValidationEventDetail>,
    fieldsWithCustomValidation?: string[],
) => {
    useEffect(() => {
        if (!source || on === RequestValidationTriggers.NEVER) {
            return
        }
        const inputs = getArrayOfValidatableInputsFromSource(source)
        assert(!!inputs, 'Could not retrieve form inputs from form. Are you sure the form exists')
        if (!inputs) {
            return
        }

        const controller = new AbortController()
        const signal = controller.signal

        inputs.forEach((input: InputWithValidation) => {
            const hasCustomValidation = !!fieldsWithCustomValidation?.filter(
                (fieldName: string) => fieldName === input.name,
            )?.length
            const event = setupRequestPartialValidationEvent(
                input.name,
                typeOfValidation,
                input.form,
                hasCustomValidation,
            )
            const dispatchDelay =
                on === RequestValidationTriggers.CHANGE ? DISPATCH_DELAY_CHANGE_TRIGGER : DISPATCH_DELAY_INPUT_TRIGGER
            input.addEventListener(
                on,
                () => {
                    dispatchRequestValidationEvent(input, event, dispatchDelay, queue)
                },
                { signal },
            )
        })

        return () => {
            controller.abort()
        }
    }, [source])
}
